import { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Select,
  InputNumber,
  Typography,
  Space,
  Row,
  Col,
  Statistic,
  Checkbox,
  Modal,
  Tooltip,
  message,
  Alert,
} from "antd";
import {
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  CalculatorOutlined,
  InfoCircleOutlined,
  EditOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { WizardStep } from "../WizardStep";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import {
  useCompanyProducts,
  CompanyProduct,
  CompanyProductInput,
} from "../../../hooks/useCompanyProducts";
import { useCompany } from "../../../hooks/useCompany";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabase";
import { debounce } from "lodash";
import { companyProductMatchesSearch } from "../../../utils/companyProductSearch";
import { BookingProductBulkImportModal } from "../BookingProductBulkImportModal";
import type { ImportedBookingProduct } from "../../../utils/bookingProductBulkImport";
import { useProductForm } from "../../../contexts/ProductFormContext";
import { getEnv } from "../../../lib/env";

// Feature flag to control whether to use product form context
const ENABLE_PRODUCT_FORM =
  getEnv("VITE_ENABLE_PRODUCT_FORM", "false") === "true";

// Custom hook to safely handle conditional ProductForm usage
const useSafeProductForm = () => {
  // Default values when provider is missing
  const defaultValues = {
    currentFormValues: {},
    updateFormValues: () => {},
    clearFormValues: () => {},
    isEditingProduct: false,
    setIsEditingProduct: () => {},
  };

  // Always call the hook to satisfy React's rules
  try {
    // Try to use the form context, and if it fails, use default values
    return useProductForm();
  } catch (err) {
    // If feature flag is enabled, log the error
    if (ENABLE_PRODUCT_FORM) {
      console.error(
        "ProductFormProvider missing but feature flag is enabled:",
        err
      );
    }
    // Return default values
    return defaultValues;
  }
};

interface ProductFormValues {
  name: string;
  hs_code: string | null;
  ean?: number | null;
  gross_weight: number | null;
  cbm: number | null;
  value: number | null;
  pieces?: number | null;
  cartons?: number | null;
  saveForLater: boolean;
}

interface BookingProduct {
  id?: number;
  name: string;
  hs_code: string | null;
  ean?: number | null;
  gross_weight: number | null;
  cbm: number | null;
  value: number | null;
  pieces?: number | null;
  cartons?: number | null;
  isNew?: boolean;
  company_product_id?: number;
}

export const ProductDetails = () => {
  const { state, updateStep, setCurrentStep } = useBookingWizard();
  const { selectedCompany } = useCompany();
  const {
    products: companyProducts,
    saveCompanyProduct,
    updateCompanyProduct,
    deleteCompanyProduct,
  } = useCompanyProducts(selectedCompany);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { updateFormValues, setIsEditingProduct } = useSafeProductForm();

  // Store persistent form values to prevent loss on re-render
  const persistentFormValues = useRef<Partial<ProductFormValues> | null>(null);

  const [products, setProducts] = useState<BookingProduct[]>(
    state.products || []
  );
  const [form] = Form.useForm<ProductFormValues>();
  const [selectedCompanyProduct, setSelectedCompanyProduct] =
    useState<CompanyProduct | null>(null);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [showWeightAlert, setShowWeightAlert] = useState(false);
  const [showWeightAlertForPieces, setShowWeightAlertForPieces] =
    useState(false);
  const [showCbmAlert, setShowCbmAlert] = useState(false);
  const [showUpdateValueModal, setShowUpdateValueModal] = useState(false);
  const [newTotalValue, setNewTotalValue] = useState<number | null>(null);
  const [newPiecesCount, setNewPiecesCount] = useState<number | null>(null);
  const [showGrossWeightAlert, setShowGrossWeightAlert] = useState(false);
  const [pendingProductData, setPendingProductData] = useState<{
    values: ProductFormValues;
    companyProductId: number;
  } | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [productToDelete, setProductToDelete] = useState<CompanyProduct | null>(
    null
  );
  const [isCbmCalculatorVisible, setIsCbmCalculatorVisible] = useState(false);
  const [isCartonCountModalVisible, setIsCartonCountModalVisible] =
    useState(false);
  const [calculatorForm] = Form.useForm();
  const [cartonCountForm] = Form.useForm();
  const [showSaveOptionsModal, setShowSaveOptionsModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<{
    values: ProductFormValues;
    newProduct: BookingProduct;
  } | null>(null);
  const [previousPieces, setPreviousPieces] = useState<number | null>(null);
  // New state for editing product in the list
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(
    null
  );
  const [editProductForm] = Form.useForm<BookingProduct>();
  const [isEditProductModalVisible, setIsEditProductModalVisible] =
    useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // First useEffect for handling preserved state on initialization
  useEffect(() => {
    // Only run this on initial load with preserved state
    const preservedFormState = location.state?.preservedFormState;
    const fromProductPage = location.state?.fromProductPage;
    const updatedProduct = location.state?.updatedProduct;

    if (preservedFormState && fromProductPage && updatedProduct) {
      console.log(
        "Initial load with preserved form state:",
        preservedFormState
      );

      // Save the preserved values to our persistent ref
      persistentFormValues.current = preservedFormState;

      // Initialize form with preserved values - this should persist
      setTimeout(() => {
        if (persistentFormValues.current) {
          form.setFieldsValue(persistentFormValues.current);
        }

        // Also set the selected product if applicable
        const product = companyProducts.find((p) => p.id === updatedProduct.id);
        if (product) {
          setSelectedCompanyProduct(product);
        }
      }, 100);
    }
  }, []); // Empty dependency array means this runs once on mount

  // Use layout effect to set form values as early as possible in the rendering cycle
  useLayoutEffect(() => {
    // Apply persistent form values at the earliest possible moment
    if (persistentFormValues.current) {
      console.log(
        "Setting form values in layout effect:",
        persistentFormValues.current
      );
      form.setFieldsValue(persistentFormValues.current);
    }

    // Also check location state directly
    const preservedFormState = location.state?.preservedFormState;
    if (preservedFormState) {
      console.log("Setting form values from location state in layout effect");
      form.setFieldsValue(preservedFormState);
    }
  }, [form, location.state]);

  // Apply persistent form values whenever form might get reset
  useEffect(() => {
    if (persistentFormValues.current) {
      console.log("Restoring from persistent form values");
      setTimeout(() => {
        form.setFieldsValue(persistentFormValues.current as ProductFormValues);
      }, 100);
    }
  }, [form]);

  // Handle returning from MyProducts and updating products
  useEffect(() => {
    const fromProductPage = location.state?.fromProductPage;
    const updatedProduct = location.state?.updatedProduct;
    const returnToStep = location.state?.returnToStep;
    const forceStep = location.state?.forceStep;
    const preservedFormState = location.state?.preservedFormState;
    const previousProducts = location.state?.previousProducts;

    // Log for debugging
    if (fromProductPage) {
      console.log("ProductDetails - Processing product updates:", {
        updatedProduct,
        returnToStep,
        forceStep,
        previousProducts: previousProducts
          ? `${previousProducts.length} products`
          : "none",
      });
    }

    // If we have form state values, apply them
    if (preservedFormState) {
      persistentFormValues.current = preservedFormState;

      // Apply form values in a timeout to ensure component is fully mounted
      setTimeout(() => {
        console.log(
          "ProductDetails - Applied form values in timeout:",
          preservedFormState
        );
        form.setFieldsValue(preservedFormState);
      }, 300);
    }

    // If we have an updated product and we came from the product page
    if (updatedProduct && fromProductPage && previousProducts) {
      // Find if the product is already in the booking
      const existingProductIndex = previousProducts.findIndex(
        (p: BookingProduct) => p.company_product_id === updatedProduct.id
      );

      if (existingProductIndex >= 0) {
        // Update the existing product in the booking
        const updatedProducts = [...previousProducts];
        updatedProducts[existingProductIndex] = {
          ...updatedProducts[existingProductIndex],
          name: updatedProduct.name,
          hs_code: updatedProduct.hs_code,
          ean: updatedProduct.ean,
          company_product_id: updatedProduct.id,
          // Explicitly keep the original values for these fields
          gross_weight: updatedProducts[existingProductIndex].gross_weight,
          cbm: updatedProducts[existingProductIndex].cbm,
          value: updatedProducts[existingProductIndex].value,
          pieces: updatedProducts[existingProductIndex].pieces,
          cartons: updatedProducts[existingProductIndex].cartons,
        };

        setProducts(updatedProducts);

        // Try again in 500ms to ensure it took effect
        setTimeout(() => {
          setProducts(updatedProducts);
        }, 500);

        // Priority: forceStep > returnToStep
        const targetStep =
          typeof forceStep === "number" ? forceStep : returnToStep;

        // Force the step to be the target step
        if (typeof targetStep === "number") {
          console.log("ProductDetails - Setting step directly to:", targetStep);

          // Use multiple timeouts with exponential backoff
          const setStepWithTimeout = (delay: number, attempt: number = 1) => {
            setTimeout(() => {
              if (state.currentStep !== targetStep) {
                console.log(
                  `ProductDetails - Setting step${
                    attempt > 1 ? " AGAIN" : " NOW"
                  } to:`,
                  targetStep
                );
                setCurrentStep(targetStep);

                // Update the state with the products as well
                updateStep(targetStep, { products: updatedProducts });

                // If we haven't tried too many times yet, schedule another attempt
                if (attempt < 5) {
                  setStepWithTimeout(delay * 1.5, attempt + 1);
                }
              }
            }, delay);
          };

          // Start the sequence
          setStepWithTimeout(200);
        } else {
          updateStep(state.currentStep, { products: updatedProducts });
        }
      }

      // Also set the selected company product
      if (updatedProduct) {
        const product = companyProducts.find((p) => p.id === updatedProduct.id);
        if (product) {
          setSelectedCompanyProduct(product);
        }
      }
    }
  }, [
    location.state,
    form,
    companyProducts,
    setCurrentStep,
    state.currentStep,
    updateStep,
  ]);

  // Initialize previous pieces when form values change
  useEffect(() => {
    const currentPieces = form.getFieldValue("pieces");
    if (currentPieces) {
      setPreviousPieces(currentPieces);
    }
  }, [form]);

  // Create a debounced function to handle pieces change
  const debouncedHandlePiecesChange = debounce((newPieces: number) => {
    const currentValue = form.getFieldValue("value") || 0;

    if (newPieces > 0 && currentValue > 0 && previousPieces) {
      const valuePerPiece = currentValue / previousPieces;
      const calculatedNewValue = Number((valuePerPiece * newPieces).toFixed(2));

      if (calculatedNewValue !== currentValue) {
        setNewTotalValue(calculatedNewValue);
        setNewPiecesCount(newPieces);
        setShowUpdateValueModal(true);
      }
    }

    setPreviousPieces(newPieces);
  }, 1500); // 1.5 second delay

  const calculateProductValues = (
    product: CompanyProduct,
    cartonCount: number
  ) => {
    const totalPieces = product.pieces_per_carton
      ? product.pieces_per_carton * cartonCount
      : null;
    const totalWeight = product.weight_per_carton
      ? product.weight_per_carton * cartonCount
      : null;
    const cbm =
      product.carton_length && product.carton_width && product.carton_height
        ? Number(
            (
              (product.carton_length *
                product.carton_width *
                product.carton_height *
                cartonCount) /
              1000000
            ).toFixed(3)
          )
        : null;
    const totalValue =
      product.unit_price && product.pieces_per_carton
        ? product.unit_price * product.pieces_per_carton * cartonCount
        : null;

    return { totalPieces, totalWeight, cbm, totalValue };
  };

  const handleCartonCountSubmit = async () => {
    try {
      const values = await cartonCountForm.validateFields();
      const cartonCount = values.cartonCount;

      if (!selectedCompanyProduct) return;

      // Get current form values to preserve any manually entered data
      const currentFormValues = form.getFieldsValue();

      // Calculate new values based on carton count
      const { totalPieces, totalWeight, cbm, totalValue } =
        calculateProductValues(selectedCompanyProduct, cartonCount);

      // Set form values, preserving any existing values that were manually entered
      form.setFieldsValue({
        name: selectedCompanyProduct.name,
        hs_code: selectedCompanyProduct.hs_code?.toString() || null,
        ean: selectedCompanyProduct.ean || null,
        // Preserve gross_weight if it was manually entered
        gross_weight: currentFormValues.gross_weight || totalWeight,
        // Preserve cbm if it was manually entered
        cbm: currentFormValues.cbm || cbm,
        // Preserve value if it was manually entered
        value: currentFormValues.value || totalValue,
        // Preserve pieces if it was manually entered
        pieces: currentFormValues.pieces || totalPieces,
        cartons: cartonCount, // Always use the new carton count
        saveForLater: true,
      });

      setIsCartonCountModalVisible(false);
      cartonCountForm.resetFields();
    } catch (error) {
      console.error("Failed to process carton count:", error);
    }
  };

  const handleCompanyProductSelect = (value: number | undefined) => {
    if (value === undefined) {
      setSelectedCompanyProduct(null);
      form.resetFields();
      return;
    }

    const product = companyProducts.find((p) => p.id === value);
    if (product) {
      setSelectedCompanyProduct(product);
      setIsCartonCountModalVisible(true);
    }
  };

  const handleSaveProductChoice = async (editNow: boolean) => {
    try {
      if (!pendingSaveData) return;

      const { values, newProduct } = pendingSaveData;

      // Create the company product data
      const companyProductData: CompanyProductInput = {
        name: values.name,
        hs_code: values.hs_code ? parseInt(values.hs_code) : null,
        ean: values.ean || null,
        company_id: selectedCompany!,
        pieces_per_carton: null,
        weight_per_carton: null,
        carton_length: null,
        carton_width: null,
        carton_height: null,
        unit_price: null,
      };

      // Save the product to company products
      const savedCompanyProduct = await saveCompanyProduct(companyProductData);

      // Create the product for the booking with the company_product_id
      const updatedProduct = {
        ...newProduct,
        company_product_id: savedCompanyProduct.id,
      };

      // Add to current booking products
      const updatedProducts = [...products, updatedProduct];
      setProducts(updatedProducts);

      // Save to booking wizard state
      await updateStep(state.currentStep, { products: updatedProducts });

      // Reset form and state
      form.resetFields();
      setSelectedCompanyProduct(null);
      setShowSaveOptionsModal(false);
      setPendingSaveData(null);

      // If user chose to edit now, navigate to My Products
      if (editNow) {
        navigate("/my-products", {
          state: {
            editProductId: savedCompanyProduct.id,
            currentStep: state.currentStep,
          },
        });
      }
    } catch (error) {
      console.error("Failed to save product:", error);
      message.error("Failed to save product");
    }
  };

  const handleConfirmUpdate = async () => {
    if (!pendingProductData) return;

    try {
      const { values, companyProductId } = pendingProductData;

      // Update the company product
      const companyProductData: Partial<CompanyProductInput> = {
        name: values.name,
        hs_code: values.hs_code ? parseInt(values.hs_code) : null,
        ean:
          values.ean && values.ean.toString().trim() !== "" ? values.ean : null,
      };

      if (selectedCompany) {
        companyProductData.company_id = selectedCompany;
      }

      await updateCompanyProduct(companyProductId, companyProductData);

      // Continue with adding the product
      // Create a new object with only the properties we want
      const newProduct: BookingProduct = {
        name: values.name,
        hs_code: values.hs_code,
        ean:
          values.ean && values.ean.toString().trim() !== "" ? values.ean : null,
        gross_weight: values.gross_weight,
        cbm: values.cbm,
        value: values.value,
        pieces: values.pieces,
        cartons: values.cartons,
        company_product_id: companyProductId,
      };

      // Update local state with the new product
      const updatedProducts = [...products, newProduct];
      setProducts(updatedProducts);

      // Save to booking wizard state immediately
      await updateStep(state.currentStep, { products: updatedProducts });

      form.resetFields();
      setSelectedCompanyProduct(null);
    } catch (error) {
      console.error("Failed to update company product:", error);
      message.error(t("bookingWizard.productDetails.errorSavingProduct"));
    } finally {
      setShowUpdateConfirm(false);
      setPendingProductData(null);
    }
  };

  const handleCancelUpdate = () => {
    setShowUpdateConfirm(false);
    setPendingProductData(null);
  };

  const handleDuplicate = (index: number) => {
    // Create a deep copy of the product
    const duplicatedProduct = { ...products[index] };

    // Remove the id to make it a new product
    delete duplicatedProduct.id;

    // Add it to the products list
    const updatedProducts = [...products, duplicatedProduct];
    setProducts(updatedProducts);

    // Show the edit modal for the newly duplicated product
    setEditingProductIndex(updatedProducts.length - 1);
    editProductForm.setFieldsValue(duplicatedProduct);
    setIsEditProductModalVisible(true);
  };

  // Add new function to handle editing a product in the list
  const handleEditListProduct = (index: number) => {
    setEditingProductIndex(index);
    editProductForm.setFieldsValue(products[index]);
    setIsEditProductModalVisible(true);
  };

  // Add function to save edited product
  const handleSaveEditedProduct = async () => {
    try {
      const values = await editProductForm.validateFields();

      // Additional validation for EAN to ensure it's exactly 13 digits if provided
      if (
        values.ean &&
        values.ean.toString().trim() !== "" &&
        String(values.ean).length !== 13
      ) {
        message.error(t("bookingWizard.productDetails.eanLength"));
        return;
      }

      // Make sure all required fields have values
      const requiredFields = [
        { key: "name", label: t("bookingWizard.productDetails.productName") },
        { key: "hs_code", label: t("bookingWizard.productDetails.hsCode") },
        { key: "pieces", label: t("bookingWizard.productDetails.totalPieces") },
        {
          key: "cartons",
          label: t("bookingWizard.productDetails.totalCartons"),
        },
        {
          key: "gross_weight",
          label: t("bookingWizard.productDetails.totalWeight"),
        },
        { key: "cbm", label: t("bookingWizard.productDetails.totalCbm") },
        { key: "value", label: t("bookingWizard.productDetails.totalValue") },
      ];

      const missingFields = requiredFields.filter(
        (field) => !values[field.key as keyof typeof values]
      );

      if (missingFields.length > 0) {
        const fieldNames = missingFields.map((f) => f.label).join(", ");
        message.error(`${t("common.required")}: ${fieldNames}`);
        return;
      }

      if (editingProductIndex !== null) {
        // Create an updated products array
        const updatedProducts = [...products];

        // Convert EAN to number type if present (to maintain data consistency)
        const processedValues = {
          ...values,
          ean:
            values.ean && values.ean.toString().trim() !== ""
              ? Number(values.ean)
              : null,
        };

        updatedProducts[editingProductIndex] = {
          ...updatedProducts[editingProductIndex],
          ...processedValues,
        };

        setProducts(updatedProducts);

        // Save to booking wizard state immediately to persist changes
        try {
          await updateStep(state.currentStep, { products: updatedProducts });
          message.success(
            t("bookingWizard.productDetails.productUpdatedSuccess")
          );
        } catch (error) {
          console.error(
            "Failed to update booking after editing product:",
            error
          );
          message.error(t("bookingWizard.productDetails.errorSavingProduct"));
        }

        setIsEditProductModalVisible(false);
        setEditingProductIndex(null);
      }
    } catch (error) {
      console.error("Error validating form:", error);
      // Form validation will display appropriate error messages
    }
  };

  const handleRemove = async (index: number) => {
    const productToRemove = products[index];
    const updatedProducts = products.filter((_, i) => i !== index);
    setProducts(updatedProducts);

    // If the product has an ID, delete it from the database
    if (productToRemove.id) {
      try {
        const { error } = await supabase
          .from("booking_product")
          .delete()
          .eq("id", productToRemove.id);

        if (error) {
          console.error("Failed to delete product from database:", error);
          message.error("Failed to delete product from database");
          // Revert local state if database deletion fails
          setProducts(products);
          return;
        }
      } catch (error) {
        console.error("Error deleting product:", error);
        message.error("Failed to delete product");
        // Revert local state if database deletion fails
        setProducts(products);
        return;
      }
    }

    // Update the booking wizard state with the new products list
    try {
      await updateStep(state.currentStep, { products: updatedProducts });
    } catch (error) {
      console.error("Failed to update booking after removing product:", error);
      message.error("Failed to update booking");
      // Revert local state if booking update fails
      setProducts(products);
    }
  };

  const calculateTotals = () => {
    return products.reduce(
      (acc, product) => ({
        totalWeight: acc.totalWeight + (product.gross_weight || 0),
        totalCbm: Number((acc.totalCbm + (product.cbm || 0)).toFixed(3)),
        totalValue: acc.totalValue + (product.value || 0),
        totalPieces: acc.totalPieces + (product.pieces || 0),
        totalCartons: acc.totalCartons + (product.cartons || 0),
      }),
      {
        totalWeight: 0,
        totalCbm: 0,
        totalValue: 0,
        totalPieces: 0,
        totalCartons: 0,
      }
    );
  };

  const handleNext = async () => {
    // Check if products array is empty
    if (products.length === 0) {
      // Show error message if no products have been added
      Modal.error({
        title: t("bookingWizard.productDetails.noProductsTitle"),
        content: t("bookingWizard.productDetails.noProductsContent"),
        okText: t("common.gotIt"),
      });
      return; // Prevent proceeding to next step
    }

    try {
      // Make a deep copy of the products array to ensure it's properly passed
      let productsCopy = JSON.parse(JSON.stringify(products));

      // Create or update booking_product records for each product
      if (state.id && state.company_id) {
        const savedProducts = [];
        for (const product of productsCopy) {
          // Create a database-ready object from the product
          const productData = {
            booking_id: state.id,
            name: product.name,
            hs_code: product.hs_code,
            gross_weight: product.gross_weight,
            cbm: product.cbm,
            value: product.value,
            pieces: product.pieces,
            cartons: product.cartons,
            ean:
              product.ean && product.ean.toString().trim() !== ""
                ? product.ean
                : null,
            company_id: state.company_id,
          };

          if (!product.id) {
            // This is a new product, insert it
            const { data: savedProduct, error: productError } = await supabase
              .from("booking_product")
              .insert(productData)
              .select()
              .single();

            if (productError) {
              console.error("Error saving product:", productError);
              throw productError;
            }

            // Keep company_product_id in local state but use database ID
            savedProducts.push({
              ...product,
              id: savedProduct.id,
            });
          } else {
            // This is an existing product, update it
            const { error: updateError } = await supabase
              .from("booking_product")
              .update(productData)
              .eq("id", product.id);

            if (updateError) {
              console.error("Error updating product:", updateError);
              throw updateError;
            }

            savedProducts.push(product);
          }
        }

        // Update the products array with the saved products
        productsCopy = savedProducts;
      }

      // Store products in local storage as a backup
      localStorage.setItem("tempProducts", JSON.stringify(productsCopy));

      // Update the step with the saved products
      await updateStep(state.currentStep + 1, { products: productsCopy });

      // Add debugging to confirm step was updated
      console.log(
        "ProductDetails - handleNext - Step updated, current step:",
        state.currentStep + 1
      );
    } catch (error) {
      console.error("Error updating step with products:", error);
      Modal.error({
        title: t("bookingWizard.productDetails.errorSavingTitle"),
        content: t("bookingWizard.productDetails.errorSavingContent"),
      });
    }
  };

  const totals = calculateTotals();

  const handleDeleteProduct = (
    product: CompanyProduct,
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation(); // Prevent the select dropdown from opening/closing
    setProductToDelete(product);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    try {
      await deleteCompanyProduct(productToDelete.id);

      // If the deleted product was selected, reset the form
      if (selectedCompanyProduct?.id === productToDelete.id) {
        setSelectedCompanyProduct(null);
        form.resetFields();
      }

      // Remove the product from any existing booking products that reference it
      setProducts((prev) =>
        prev.map((product) =>
          product.company_product_id === productToDelete.id
            ? { ...product, company_product_id: undefined }
            : product
        )
      );

      setDeleteModalVisible(false);
      setProductToDelete(null);
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setProductToDelete(null);
  };

  const handleCalculateCbm = () => {
    const cartons = form.getFieldValue("cartons");
    calculatorForm.setFieldsValue({ cartons });
    setIsCbmCalculatorVisible(true);
  };

  const handleCbmCalculation = async () => {
    try {
      const values = await calculatorForm.validateFields();
      const { length, width, height, cartons } = values;
      // Convert to meters (assuming input is in cm) and round to 3 decimals
      const cbmPerBox = Number(
        ((length * width * height) / 1000000).toFixed(3)
      ); // Convert cm³ to m³
      const totalCbm = Number((cbmPerBox * cartons).toFixed(3));

      form.setFieldsValue({ cbm: totalCbm });
      setIsCbmCalculatorVisible(false);
      calculatorForm.resetFields();
    } catch (error) {
      console.error("Failed to calculate CBM:", error);
    }
  };

  const handleEditProduct = (
    product: CompanyProduct,
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation(); // Prevent the select dropdown from opening/closing

    // Get current form values to preserve them
    const currentFormValues = form.getFieldsValue();

    // Get the product's values currently in the booking, if any
    const existingProduct = products.find(
      (p) => p.company_product_id === product.id
    );

    // Combine all values to preserve when navigating
    const preservedFormState = {
      ...currentFormValues,
      // Use existing values from the booking product if available
      gross_weight:
        existingProduct?.gross_weight || currentFormValues.gross_weight,
      cbm: existingProduct?.cbm || currentFormValues.cbm,
      value: existingProduct?.value || currentFormValues.value,
      pieces: existingProduct?.pieces || currentFormValues.pieces,
      cartons: existingProduct?.cartons || currentFormValues.cartons,
    };

    console.log("Preserving form state:", preservedFormState);

    navigate("/my-products", {
      state: {
        editProductId: product.id,
        currentStep: state.currentStep,
        formState: preservedFormState,
        bookingId: state.id, // Pass the booking ID
        previousProducts: products, // Pass the current products list
      },
    });
  };

  const handleSubmit = async (values: ProductFormValues) => {
    try {
      // When submitting the form, also save the values to our persistent ref
      persistentFormValues.current = values;

      // Process the form values into a product for the booking
      const newProduct: BookingProduct = {
        name: values.name,
        hs_code: values.hs_code,
        ean: values.ean,
        gross_weight: values.gross_weight,
        cbm: values.cbm,
        value: values.value,
        pieces: values.pieces,
        cartons: values.cartons,
        isNew: true,
      };

      // If a company product was selected, link it
      if (selectedCompanyProduct) {
        newProduct.company_product_id = selectedCompanyProduct.id;
      }

      // Check if we should save this as a company product for later use
      if (values.saveForLater && !selectedCompanyProduct) {
        // Show the save options modal
        setPendingSaveData({
          values,
          newProduct,
        });
        setShowSaveOptionsModal(true);
        return;
      }

      // Update local state with the new product
      const updatedProducts = [...products, newProduct];
      setProducts(updatedProducts);

      // Save to booking wizard state immediately
      await updateStep(state.currentStep, { products: updatedProducts });

      // Reset form and selection
      form.resetFields();
      setSelectedCompanyProduct(null);
      updateFormValues({});
      setIsEditingProduct(false);
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      message.error(t("bookingWizard.productDetails.errorSavingProduct"));
    }
  };

  const handleUpdateValue = () => {
    if (newTotalValue !== null) {
      form.setFieldsValue({ value: newTotalValue });
    }
    setShowUpdateValueModal(false);
    setNewTotalValue(null);
    setNewPiecesCount(null);
    setShowGrossWeightAlert(true);
  };

  const handleCancelUpdateValue = () => {
    setShowUpdateValueModal(false);
    setNewTotalValue(null);
    setNewPiecesCount(null);
    setShowGrossWeightAlert(true);
  };

  return (
    <WizardStep
      onNext={handleNext}
      onPrevious={() => updateStep(state.currentStep - 1, {})}
      nextDisabled={products.length === 0}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card title={t("bookingWizard.productDetails.addProduct")}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Row gutter={16}>
              <Col span={24} style={{ marginBottom: 16 }}>
                <Form.Item
                  label={t(
                    "bookingWizard.productDetails.selectFromSavedProducts"
                  )}
                >
                  <Select
                    showSearch
                    placeholder={t(
                      "bookingWizard.productDetails.searchSavedProductPlaceholder"
                    )}
                    style={{ width: "100%" }}
                    allowClear
                    onChange={handleCompanyProductSelect}
                    value={selectedCompanyProduct?.id}
                    optionFilterProp="value"
                    filterOption={(input, option) => {
                      const product = companyProducts.find(
                        (p) => p.id === option?.value
                      );
                      return product
                        ? companyProductMatchesSearch(product, input)
                        : false;
                    }}
                  >
                    {companyProducts.map((product) => (
                      <Select.Option key={product.id} value={product.id}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div>
                            <strong>{product.name}</strong>
                            {product.ean && (
                              <span style={{ marginLeft: 8, color: "#8c8c8c" }}>
                                EAN: {product.ean}
                              </span>
                            )}
                          </div>
                          <Space>
                            <Tooltip
                              title={t(
                                "bookingWizard.productDetails.editProduct"
                              )}
                            >
                              <EditOutlined
                                style={{ color: "#1890ff" }}
                                onClick={(e) => handleEditProduct(product, e)}
                              />
                            </Tooltip>
                            <Tooltip
                              title={t(
                                "bookingWizard.productDetails.deleteProduct"
                              )}
                            >
                              <Space>
                                <DeleteOutlined
                                  style={{ color: "#ff4d4f" }}
                                  onClick={(e) =>
                                    handleDeleteProduct(product, e)
                                  }
                                />
                              </Space>
                            </Tooltip>
                          </Space>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                  <Button
                    type="dashed"
                    icon={<UploadOutlined />}
                    onClick={() => setBulkImportOpen(true)}
                    style={{ marginTop: 8 }}
                    block
                  >
                    {t("bookingWizard.productBulkImport.openButton")}
                  </Button>
                </Form.Item>
              </Col>
            </Row>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              onValuesChange={(changedValues, allValues) => {
                // Update the form context with current values
                updateFormValues(allValues);
                setIsEditingProduct(true);

                // Original logic for pieces and cartons changes
                if ("pieces" in changedValues) {
                  const newPieces = changedValues.pieces || 0;
                  debouncedHandlePiecesChange(newPieces);
                }

                if ("cartons" in changedValues) {
                  setShowWeightAlert(true);
                  setShowCbmAlert(true);
                }
              }}
            >
              {/* Product Identification Section */}
              <Card
                size="small"
                title={t("bookingWizard.productDetails.productIdentification")}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label={
                        <Space>
                          {t("bookingWizard.productDetails.productName")}
                          <Tooltip
                            title={t(
                              "bookingWizard.productDetails.productNameTooltip"
                            )}
                          >
                            <InfoCircleOutlined style={{ fontSize: "12px" }} />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.productDetails.productNameRequired"
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t(
                          "bookingWizard.productDetails.productNamePlaceholder"
                        )}
                        disabled={!!selectedCompanyProduct}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="hs_code"
                      label={
                        <Space>
                          {t("bookingWizard.productDetails.hsCode")}
                          <Tooltip
                            title={t(
                              "bookingWizard.productDetails.hsCodeTooltip"
                            )}
                          >
                            <InfoCircleOutlined style={{ fontSize: "12px" }} />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Input
                        placeholder={t(
                          "bookingWizard.productDetails.hsCodePlaceholder"
                        )}
                        disabled={!!selectedCompanyProduct}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="ean"
                      label={
                        <Space>
                          {t("bookingWizard.productDetails.ean")}
                          <Tooltip
                            title={t("bookingWizard.productDetails.eanTooltip")}
                          >
                            <InfoCircleOutlined style={{ fontSize: "12px" }} />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value) {
                              const eanStr = String(value);
                              if (eanStr.length !== 13) {
                                return Promise.reject(
                                  new Error(
                                    t("bookingWizard.productDetails.eanLength")
                                  )
                                );
                              }
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        style={{ width: "100%" }}
                        maxLength={13}
                        placeholder={t(
                          "bookingWizard.productDetails.eanPlaceholder"
                        )}
                        onChange={(e) => {
                          // Only allow digits
                          const value = e.target.value.replace(/[^\d]/g, "");
                          e.target.value = value;
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Quantity Section */}
              <Card
                size="small"
                title={t("bookingWizard.productDetails.quantityInformation")}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="pieces"
                      label={
                        <Space>
                          {t("bookingWizard.productDetails.totalPieces")}
                          <Tooltip
                            title={t(
                              "bookingWizard.productDetails.totalPiecesTooltip"
                            )}
                          >
                            <InfoCircleOutlined style={{ fontSize: "12px" }} />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.productDetails.totalPiecesRequired"
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={1}
                        step={1}
                        precision={0}
                        placeholder={t(
                          "bookingWizard.productDetails.totalPiecesPlaceholder"
                        )}
                        onBlur={() => {
                          debouncedHandlePiecesChange.cancel();
                          const newPieces = form.getFieldValue("pieces") || 0;
                          const currentValue = form.getFieldValue("value") || 0;

                          if (
                            newPieces > 0 &&
                            currentValue > 0 &&
                            previousPieces
                          ) {
                            const valuePerPiece = currentValue / previousPieces;
                            const calculatedNewValue = Number(
                              (valuePerPiece * newPieces).toFixed(2)
                            );

                            if (calculatedNewValue !== currentValue) {
                              setNewTotalValue(calculatedNewValue);
                              setNewPiecesCount(newPieces);
                              setShowUpdateValueModal(true);
                            }
                          }

                          setPreviousPieces(newPieces);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="cartons"
                      label={
                        <Space>
                          {t("bookingWizard.productDetails.totalCartons")}
                          <Tooltip
                            title={t(
                              "bookingWizard.productDetails.totalCartonsTooltip"
                            )}
                          >
                            <InfoCircleOutlined style={{ fontSize: "12px" }} />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.productDetails.totalCartonsRequired"
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={1}
                        step={1}
                        precision={0}
                        placeholder={t(
                          "bookingWizard.productDetails.totalCartonsPlaceholder"
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Measurements Section */}
              <Card
                size="small"
                title={t("bookingWizard.productDetails.measurementsAndValue")}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="gross_weight"
                      label={
                        <Space>
                          {t("bookingWizard.productDetails.totalGrossWeight")}
                          <Tooltip
                            title={t(
                              "bookingWizard.productDetails.totalGrossWeightTooltip"
                            )}
                          >
                            <InfoCircleOutlined style={{ fontSize: "12px" }} />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.productDetails.grossWeightRequired"
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0.1}
                        step={0.1}
                        placeholder={t(
                          "bookingWizard.productDetails.totalGrossWeightPlaceholder"
                        )}
                        addonAfter="kg"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab") {
                            const input = e.target as HTMLInputElement;
                            const value = input.value;
                            if (value.includes(",")) {
                              e.preventDefault();
                              const numericValue = parseFloat(
                                value.replace(",", ".")
                              );
                              form.setFieldValue("gross_weight", numericValue);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value.includes(",")) {
                            const numericValue = parseFloat(
                              value.replace(",", ".")
                            );
                            form.setFieldValue("gross_weight", numericValue);
                          }
                        }}
                      />
                    </Form.Item>
                    {showGrossWeightAlert && (
                      <Alert
                        message={t(
                          "bookingWizard.productDetails.checkGrossWeight"
                        )}
                        type="warning"
                        showIcon
                        closable
                        onClose={() => setShowGrossWeightAlert(false)}
                        action={
                          <Button
                            size="small"
                            onClick={() => setShowGrossWeightAlert(false)}
                          >
                            {t("common.dismiss")}
                          </Button>
                        }
                        style={{ marginBottom: 16 }}
                      />
                    )}
                    {selectedCompanyProduct && showWeightAlertForPieces && (
                      <div style={{ marginTop: 8, marginBottom: 16 }}>
                        <Alert
                          message={t(
                            "bookingWizard.productDetails.checkTotalWeight"
                          )}
                          type="warning"
                          showIcon
                          closable
                          onClose={() => setShowWeightAlertForPieces(false)}
                          action={
                            <Button
                              size="small"
                              onClick={() => setShowWeightAlertForPieces(false)}
                            >
                              {t("common.dismiss")}
                            </Button>
                          }
                        />
                      </div>
                    )}
                    {selectedCompanyProduct && showWeightAlert && (
                      <div style={{ marginTop: 8, marginBottom: 16 }}>
                        <Alert
                          message={t(
                            "bookingWizard.productDetails.updateTotalWeight"
                          )}
                          type="warning"
                          showIcon
                          closable
                          onClose={() => setShowWeightAlert(false)}
                          action={
                            <Button
                              size="small"
                              onClick={() => setShowWeightAlert(false)}
                            >
                              {t("common.dismiss")}
                            </Button>
                          }
                        />
                      </div>
                    )}
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="cbm"
                      label={
                        <Space>
                          {t("bookingWizard.productDetails.totalCbm")}
                          <Tooltip
                            title={t(
                              "bookingWizard.productDetails.totalCbmTooltip"
                            )}
                          >
                            <CalculatorOutlined
                              style={{ fontSize: "12px", cursor: "pointer" }}
                              onClick={handleCalculateCbm}
                            />
                          </Tooltip>
                        </Space>
                      }
                      rules={[
                        {
                          required: true,
                          message: t(
                            "bookingWizard.productDetails.cbmRequired"
                          ),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0.001}
                        step={0.001}
                        placeholder={t(
                          "bookingWizard.productDetails.totalCbmPlaceholder"
                        )}
                        addonAfter="m³"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab") {
                            const input = e.target as HTMLInputElement;
                            const value = input.value;
                            if (value.includes(",")) {
                              e.preventDefault();
                              const numericValue = parseFloat(
                                value.replace(",", ".")
                              );
                              form.setFieldValue("cbm", numericValue);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value.includes(",")) {
                            const numericValue = parseFloat(
                              value.replace(",", ".")
                            );
                            form.setFieldValue("cbm", numericValue);
                          }
                        }}
                      />
                    </Form.Item>
                    {selectedCompanyProduct && showCbmAlert && (
                      <div style={{ marginTop: 8, marginBottom: 16 }}>
                        <Alert
                          message={t(
                            "bookingWizard.productDetails.checkTotalCbm"
                          )}
                          type="warning"
                          showIcon
                          closable
                          onClose={() => setShowCbmAlert(false)}
                          action={
                            <Button
                              size="small"
                              onClick={() => setShowCbmAlert(false)}
                            >
                              {t("common.dismiss")}
                            </Button>
                          }
                        />
                      </div>
                    )}
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="value"
                      label={
                        <Space>
                          {t("bookingWizard.productDetails.totalValue")}
                          <Tooltip
                            title={t(
                              "bookingWizard.productDetails.totalValueTooltip"
                            )}
                          >
                            <InfoCircleOutlined style={{ fontSize: "12px" }} />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        placeholder={t(
                          "bookingWizard.productDetails.totalValuePlaceholder"
                        )}
                        prefix="$"
                        precision={2}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === "Tab") {
                            const input = e.target as HTMLInputElement;
                            const value = input.value;
                            if (value.includes(",")) {
                              e.preventDefault();
                              const numericValue = parseFloat(
                                value.replace(",", ".")
                              );
                              form.setFieldValue("value", numericValue);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value.includes(",")) {
                            const numericValue = parseFloat(
                              value.replace(",", ".")
                            );
                            form.setFieldValue("value", numericValue);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {!selectedCompanyProduct && (
                <Form.Item name="saveForLater" valuePropName="checked">
                  <Checkbox>
                    {t("bookingWizard.productDetails.saveForLater")}
                  </Checkbox>
                </Form.Item>
              )}

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => form.validateFields().then(handleSubmit)}
              >
                {t("bookingWizard.productDetails.addProduct")}
              </Button>
            </Form>
          </Space>
        </Card>

        {products.length > 0 && (
          <>
            <Typography.Title level={5}>
              {t("bookingWizard.productDetails.productsList")}
            </Typography.Title>
            <Row gutter={[16, 16]}>
              {products.map((product, index) => (
                <Col span={12} key={index}>
                  <Card
                    size="small"
                    title={product.name}
                    extra={
                      <Space>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => handleEditListProduct(index)}
                          title={t("bookingWizard.productDetails.editProduct")}
                        />
                        <Button
                          icon={<CopyOutlined />}
                          onClick={() => handleDuplicate(index)}
                          title={t(
                            "bookingWizard.productDetails.duplicateProduct"
                          )}
                        />
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemove(index)}
                          title={t(
                            "bookingWizard.productDetails.removeProduct"
                          )}
                        />
                      </Space>
                    }
                  >
                    <Row gutter={[16, 8]}>
                      <Col span={12}>
                        <Typography.Text type="secondary">
                          {t("bookingWizard.productDetails.hsCodeLabel")}
                        </Typography.Text>
                        <div>{product.hs_code || "N/A"}</div>
                      </Col>
                      <Col span={12}>
                        <Typography.Text type="secondary">
                          {t("bookingWizard.productDetails.eanLabel")}
                        </Typography.Text>
                        <div>{product.ean || "N/A"}</div>
                      </Col>
                      <Col span={12}>
                        <Typography.Text type="secondary">
                          {t("bookingWizard.productDetails.totalPiecesLabel")}
                        </Typography.Text>
                        <div>{product.pieces || "N/A"}</div>
                      </Col>
                      <Col span={12}>
                        <Typography.Text type="secondary">
                          {t("bookingWizard.productDetails.totalCartonsLabel")}
                        </Typography.Text>
                        <div>{product.cartons || "N/A"}</div>
                      </Col>
                      <Col span={12}>
                        <Typography.Text type="secondary">
                          {t("bookingWizard.productDetails.totalWeightLabel")}
                        </Typography.Text>
                        <div>
                          {product.gross_weight
                            ? `${product.gross_weight} kg`
                            : "N/A"}
                        </div>
                      </Col>
                      <Col span={12}>
                        <Typography.Text type="secondary">
                          {t("bookingWizard.productDetails.totalCbmLabel")}
                        </Typography.Text>
                        <div>{product.cbm ? `${product.cbm} m³` : "N/A"}</div>
                      </Col>
                      <Col span={12}>
                        <Typography.Text type="secondary">
                          {t("bookingWizard.productDetails.totalValueLabel")}
                        </Typography.Text>
                        <div>
                          {product.value
                            ? `$${product.value.toFixed(2)}`
                            : "N/A"}
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Add the totals summary card */}
            <Card
              size="small"
              title={t("bookingWizard.productDetails.totalsSummary")}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Statistic
                    title={t("bookingWizard.productDetails.totalPieces")}
                    value={totals.totalPieces}
                    suffix={t("bookingWizard.productDetails.pieces")}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic
                    title={t("bookingWizard.productDetails.totalCartons")}
                    value={totals.totalCartons}
                    suffix={t("bookingWizard.productDetails.cartons")}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic
                    title={t("bookingWizard.productDetails.totalWeight")}
                    value={totals.totalWeight}
                    suffix="kg"
                    precision={1}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic
                    title={t("bookingWizard.productDetails.totalCbm")}
                    value={totals.totalCbm}
                    suffix="m³"
                    precision={3}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic
                    title={t("bookingWizard.productDetails.totalValue")}
                    value={totals.totalValue}
                    prefix="$"
                    precision={2}
                  />
                </Col>
              </Row>
            </Card>
          </>
        )}
      </Space>

      <Modal
        title="Update Company Product"
        open={showUpdateConfirm}
        onOk={handleConfirmUpdate}
        onCancel={handleCancelUpdate}
        okText="Update"
        cancelText="Cancel"
      >
        <p>{t("bookingWizard.productDetails.updateForFutureBookings")}</p>
      </Modal>

      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 16 }}
        >
          <ExclamationCircleOutlined
            style={{ color: "#faad14", fontSize: 22, marginRight: 16 }}
          />
          <Typography.Text>
            Are you sure you want to delete the product{" "}
            <strong>{productToDelete?.name}</strong>?
          </Typography.Text>
        </div>
        <Typography.Text type="secondary">
          This action cannot be undone. This will permanently delete the product
          from your company's saved products.
        </Typography.Text>
      </Modal>

      {/* CBM Calculator Modal */}
      <Modal
        title={t("bookingWizard.productDetails.calculateCbm")}
        open={isCbmCalculatorVisible}
        onOk={handleCbmCalculation}
        onCancel={() => {
          setIsCbmCalculatorVisible(false);
          calculatorForm.resetFields();
        }}
        okText={t("bookingWizard.productDetails.calculate")}
        cancelText={t("common.cancel")}
      >
        <Form form={calculatorForm} layout="vertical">
          <Form.Item
            name="cartons"
            label={t("bookingWizard.productDetails.cartonCount")}
            rules={[
              {
                required: true,
                message: t("bookingWizard.productDetails.cartonsRequired"),
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              placeholder={t("bookingWizard.productDetails.cartonCount")}
            />
          </Form.Item>

          <Typography.Text
            type="secondary"
            style={{ marginBottom: 16, display: "block" }}
          >
            {t("bookingWizard.productDetails.enterDimensions")}
          </Typography.Text>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="length"
                label={t("bookingWizard.productDetails.length")}
                rules={[
                  {
                    required: true,
                    message: t("bookingWizard.productDetails.required"),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0.1}
                  placeholder="cm"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="width"
                label={t("bookingWizard.productDetails.width")}
                rules={[
                  {
                    required: true,
                    message: t("bookingWizard.productDetails.required"),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0.1}
                  placeholder="cm"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="height"
                label={t("bookingWizard.productDetails.height")}
                rules={[
                  {
                    required: true,
                    message: t("bookingWizard.productDetails.required"),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0.1}
                  placeholder="cm"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={t("bookingWizard.productDetails.cartonCountTitle")}
        open={isCartonCountModalVisible}
        onOk={handleCartonCountSubmit}
        onCancel={() => {
          setIsCartonCountModalVisible(false);
          cartonCountForm.resetFields();
          setSelectedCompanyProduct(null);
        }}
      >
        <Form form={cartonCountForm} layout="vertical">
          <Form.Item
            name="cartonCount"
            label={t("bookingWizard.productDetails.cartonCountLabel")}
            rules={[
              {
                required: true,
                message: t("bookingWizard.productDetails.cartonCountRequired"),
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              placeholder={t(
                "bookingWizard.productDetails.cartonCountPlaceholder"
              )}
            />
          </Form.Item>
          {selectedCompanyProduct && (
            <div style={{ marginTop: 16 }}>
              <Typography.Text type="secondary">
                {t("bookingWizard.productDetails.productDetailsPerCarton")}
              </Typography.Text>
              <ul style={{ marginTop: 8 }}>
                {selectedCompanyProduct.pieces_per_carton && (
                  <li>
                    {t("bookingWizard.productDetails.piecesPerCarton")}:{" "}
                    {selectedCompanyProduct.pieces_per_carton}
                  </li>
                )}
                {selectedCompanyProduct.weight_per_carton && (
                  <li>
                    {t("bookingWizard.productDetails.weightPerCarton")}:{" "}
                    {selectedCompanyProduct.weight_per_carton} kg
                  </li>
                )}
                {selectedCompanyProduct.carton_length &&
                  selectedCompanyProduct.carton_width &&
                  selectedCompanyProduct.carton_height && (
                    <li>
                      {t("bookingWizard.productDetails.dimensionsPerCarton")}:{" "}
                      {selectedCompanyProduct.carton_length} ×{" "}
                      {selectedCompanyProduct.carton_width} ×{" "}
                      {selectedCompanyProduct.carton_height} cm
                    </li>
                  )}
                {selectedCompanyProduct.unit_price && (
                  <li>
                    {t("bookingWizard.productDetails.unitPricePerCarton")}: $
                    {selectedCompanyProduct.unit_price}
                  </li>
                )}
              </ul>
            </div>
          )}
        </Form>
      </Modal>

      {/* Add the new modal */}
      <Modal
        title="Save Product Options"
        open={showSaveOptionsModal}
        footer={null}
        onCancel={() => {
          setShowSaveOptionsModal(false);
          setPendingSaveData(null);
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text>
            Would you like to add additional product information now
            (dimensions, pieces per carton, etc.) or save the basic details and
            add this information later?
          </Typography.Text>
          <Space
            style={{ width: "100%", justifyContent: "flex-end", marginTop: 16 }}
          >
            <Button onClick={() => handleSaveProductChoice(false)}>
              Save Basic Details Only
            </Button>
            <Button
              type="primary"
              onClick={() => handleSaveProductChoice(true)}
            >
              Add Additional Info Now
            </Button>
          </Space>
        </Space>
      </Modal>

      {/* Add the update value modal */}
      <Modal
        title={t("bookingWizard.productDetails.updateValueTitle")}
        open={showUpdateValueModal}
        onOk={handleUpdateValue}
        onCancel={handleCancelUpdateValue}
        okText={t("bookingWizard.productDetails.updateValue")}
        cancelText={t("bookingWizard.productDetails.keepValue")}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text>
            {t("bookingWizard.productDetails.updateValueMessage")}
          </Typography.Text>
          <Typography.Text>
            {t("bookingWizard.productDetails.newPieces")}{" "}
            {newPiecesCount?.toLocaleString() || "0"}
          </Typography.Text>
          <Typography.Text>
            {t("bookingWizard.productDetails.newValue").replace("$", "")} ${" "}
            {newTotalValue?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </Typography.Text>
        </Space>
      </Modal>

      {/* Add Edit Product Modal */}
      <Modal
        title={t("bookingWizard.productDetails.editProduct")}
        open={isEditProductModalVisible}
        onOk={handleSaveEditedProduct}
        onCancel={() => {
          setIsEditProductModalVisible(false);
          setEditingProductIndex(null);
        }}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
      >
        <Form form={editProductForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("bookingWizard.productDetails.productName")}
            rules={[
              {
                required: true,
                message: t("bookingWizard.productDetails.nameRequired"),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="hs_code"
            label={t("bookingWizard.productDetails.hsCode")}
            rules={[
              {
                required: true,
                message: t("bookingWizard.productDetails.hsCodeRequired"),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="ean"
            label={t("bookingWizard.productDetails.ean")}
            rules={[
              {
                required: true,
                message: t("bookingWizard.productDetails.eanRequired"),
              },
              {
                validator: (_, value) => {
                  if (value) {
                    const eanStr = String(value);
                    if (eanStr.length !== 13) {
                      return Promise.reject(
                        new Error(t("bookingWizard.productDetails.eanLength"))
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              style={{ width: "100%" }}
              maxLength={13}
              placeholder={t("bookingWizard.productDetails.eanPlaceholder")}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/[^\d]/g, "");
                e.target.value = value;
              }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="pieces"
                label={t("bookingWizard.productDetails.totalPieces")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "bookingWizard.productDetails.totalPiecesRequired"
                    ),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  step={1}
                  precision={0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cartons"
                label={t("bookingWizard.productDetails.totalCartons")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "bookingWizard.productDetails.totalCartonsRequired"
                    ),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={1}
                  step={1}
                  precision={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="gross_weight"
                label={t("bookingWizard.productDetails.totalWeight")}
                rules={[
                  {
                    required: true,
                    message: t("bookingWizard.productDetails.weightRequired"),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0.1}
                  addonAfter="kg"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="cbm"
                label={t("bookingWizard.productDetails.totalCbm")}
                rules={[
                  {
                    required: true,
                    message: t("bookingWizard.productDetails.cbmRequired"),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0.001}
                  step={0.001}
                  addonAfter="m³"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="value"
                label={t("bookingWizard.productDetails.totalValue")}
                rules={[
                  {
                    required: true,
                    message: t("bookingWizard.productDetails.valueRequired"),
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  prefix="$"
                  precision={2}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <BookingProductBulkImportModal
        open={bulkImportOpen}
        onCancel={() => setBulkImportOpen(false)}
        onImport={async (imported: ImportedBookingProduct[]) => {
          const updatedProducts = [...products, ...imported];
          setProducts(updatedProducts);
          setBulkImportOpen(false);
          try {
            await updateStep(state.currentStep, { products: updatedProducts });
          } catch (error) {
            console.error("Failed to save imported products:", error);
            message.error(t("bookingWizard.productDetails.errorSavingProduct"));
          }
        }}
      />
    </WizardStep>
  );
};
